import { NInput, NText } from 'naive-ui';
import { defineComponent, onMounted, ref, watch, type PropType } from 'vue';
import { Messages } from '@/constants';

const MAX_INPUT_LENGTH = 11;

export const AppInputDate = defineComponent({
	name: 'AppInputDate',
	props: {
		modelValue: String,
		filled: Boolean,
		disabled: Boolean,
		readonly: Boolean,
		placeholder: String,
		label: String,
		ariaLabel: String,
		status: String as PropType<'success' | 'warning' | 'error'>,
		},
	emits: ['update:modelValue', 'update:value', 'blur', 'focus'],

	setup(props, { emit }) {
		const maskedValue = ref('');
		const internalError = ref('');

		if (props.modelValue) fillField(props.modelValue);

		watch(
			() => props.modelValue,
			(newValue) => {
				if (!newValue) {
					if (maskedValue.value && !internalError.value) {
						maskedValue.value = '';
						internalError.value = '';
					}
					return;
				}
		
				const dateParsed = parseDate(newValue);
		
				if (dateParsed) {
					const formattedIncoming = formatBrDate(dateParsed);

					if (maskedValue.value === formattedIncoming) {
						return; 
					}
		
					maskedValue.value = formattedIncoming;
					internalError.value = '';
				} else {
					if (maskedValue.value !== newValue) {
						 maskedValue.value = newValue;
					}
				}
			}
		);

		onMounted(() => {
			if (props.filled && !props.modelValue) {
				const today = formatBrDate(new Date());
				handleInput(today);
			}
		});

		function pad2(n: number) {
			return String(n).padStart(2, '0');
		}

		/**
		 * Parse "date-only" strings without timezone shifting.
		 * - Accepts "YYYY-MM-DD" (or with timestamp: "YYYY-MM-DDT...")
		 * - Accepts "DD/MM/YYYY"
		 * Returns a Date constructed in local time: new Date(year, monthIndex, day)
		 */
		function parseDate(value: string): Date | null {
			if (!value) return null;
			const trimmed = value.trim();

			// ISO-ish: "YYYY-MM-DD" (optionally with time portion)
			if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
				const datePart = (trimmed.split('T')[0] || '').slice(0, 10);
				const [yearStr, monthStr, dayStr] = datePart.split('-');
				if (!yearStr || !monthStr || !dayStr) return null;
				const year = Number(yearStr);
				const month = Number(monthStr);
				const day = Number(dayStr);
				if (!year || !month || !day) return null;

				const d = new Date(year, month - 1, day);
				// Validate (catches invalid dates like 31/02)
				if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day)
					return null;
				return d;
			}

			// BR: "DD/MM/YYYY"
			if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) {
				const [dayStr, monthStr, yearStr] = trimmed.split('/');
				if (!yearStr || !monthStr || !dayStr) return null;
				const year = Number(yearStr);
				const month = Number(monthStr);
				const day = Number(dayStr);
				if (!year || !month || !day) return null;

				const d = new Date(year, month - 1, day);
				if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day)
					return null;
				return d;
			}

			return null;
		}

		function formatIsoDate(date: Date) {
			return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
		}

		function formatBrDate(date: Date) {
			return `${pad2(date.getDate())}/${pad2(date.getMonth() + 1)}/${date.getFullYear()}`;
		}

		function fillField(value: string) {
            if (value == '0001-01-01T00:00:00+00:00') return;
            const dateParsed = parseDate(value);
            if (dateParsed) {
                maskedValue.value = formatBrDate(dateParsed);
            } else {
                maskedValue.value = value || '';
            }
        }

		function handleInput(value: string | undefined) {
			maskedValue.value = value || '';
			internalError.value = '';
			
			if (!value) {
                maskedValue.value = '';
                emit('update:modelValue', null);
                emit('update:value', null);
                return;
            }

			if (value.length == 10) {
				const dateParsed = parseDate(value);
				if (dateParsed) {
					const isoDate = formatIsoDate(dateParsed);

					if (isoDate !== props.modelValue) {
						emit('update:modelValue', isoDate);
						emit('update:value', isoDate);
					}

				} else {

					if (props.modelValue) {
						emit('update:modelValue', null);
						emit('update:value', null);
					}

					internalError.value = Messages.ERROR_INVALID_DATE;
				}
			}
		}

		function handleBlur(e: FocusEvent) {
            emit('blur', e);
            
            const val = maskedValue.value;
            if (val && val.length > 0) {
                if (val.length < 10) {
                    internalError.value = Messages.ERROR_INVALID_DATE;
                } else if (!parseDate(val)) {
                    internalError.value = Messages.ERROR_INVALID_DATE;
                }
            }
        }

		function handleFocus(e: FocusEvent) {
			emit('focus', e);
		}

		function handleClear() {
            internalError.value = '';
            maskedValue.value = '';
            emit('update:modelValue', null);
            emit('update:value', null);
        }

		return () => (
			<div style={{ width: '100%' }}>
				<NInput
					v-maska
					disabled={props.disabled}
					readonly={props.readonly}
					status={internalError.value ? 'error' : props.status}
					input-props={{
						'data-maska': '##/##/####',
						inputmode: 'numeric',
						'aria-label': props.ariaLabel || props.label || props.placeholder || 'Data',
					}}
					v-model:value={maskedValue.value}
					onInput={handleInput}
					onBlur={handleBlur}
					onFocus={handleFocus}
					placeholder={props.placeholder || 'DD/MM/AAAA'}
					clearable
					onClear={handleClear}
				/>
				{internalError.value && (
                    <div style={{ marginTop: '4px', lineHeight: '1.2' }}>
                        <NText type="error" depth={3} style={{ fontSize: '12px' }}>
                            {internalError.value}
                        </NText>
                    </div>
                )}
			</div>
		);
	},
});

export default AppInputDate;
